import React, { useState, useEffect } from 'react';
import { Save, Loader2, Plus, Trash2, KeyRound } from 'lucide-react';
import { Button } from '../../components/ui/button';
import { Input } from '../../components/ui/input';
import { Label } from '../../components/ui/label';
import { Card, CardContent, CardHeader, CardTitle } from '../../components/ui/card';
import { Textarea } from '../../components/ui/textarea';
import { useToast } from '../../hooks/use-toast';
import { useSettings } from '../../context/SettingsContext';
import { adminApi } from '../../context/AdminContext';
import { useAdmin } from '../../context/AdminContext';
import axios from 'axios';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;

const AdminSettings = () => {
  const { toast } = useToast();
  const { refreshSettings } = useSettings();
  const { adminUser, login: adminLogin } = useAdmin();
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [savingCreds, setSavingCreds] = useState(false);
  const [creds, setCreds] = useState({ current_password: '', new_username: '', new_password: '', confirm_password: '' });
  const [settings, setSettings] = useState({
    site_name: '', tagline: '', logo_url: '', primary_color: '#dc2626',
    phone: '', email: '', order_notification_email: '', address: '', hours: '',
    facebook: '', instagram: '', youtube: '', whatsapp: '',
    meta_title: '', meta_description: '', keywords: '',
    map_lat: 26.1, map_lng: 50.5, map_zoom: 15,
    recaptcha_site_key: '', recaptcha_secret_key: '',
    contact_cards: []
  });

  useEffect(() => { loadSettings(); }, []);

  const loadSettings = async () => {
    try {
      const response = await axios.get(`${BACKEND_URL}/api/settings`);
      const data = response.data;
      setSettings(prev => ({ ...prev, ...data }));
    } catch (error) {
      console.error('Error loading settings:', error);
    } finally {
      setLoading(false);
    }
  };

  const handleSave = async () => {
    setSaving(true);
    try {
      const payload = { ...settings };
      if (!payload.recaptcha_secret_key) {
        delete payload.recaptcha_secret_key;
      }
      await adminApi.put(`/settings`, payload);
      await refreshSettings();
      toast({ title: "Success", description: "Settings saved successfully" });
    } catch (error) {
      toast({ title: "Error", description: "Failed to save settings", variant: "destructive" });
    } finally {
      setSaving(false);
    }
  };

  const handleSaveCredentials = async () => {
    if (creds.new_password && creds.new_password !== creds.confirm_password) {
      toast({ title: 'Error', description: 'New passwords do not match', variant: 'destructive' });
      return;
    }
    if (creds.new_password && creds.new_password.length < 6) {
      toast({ title: 'Error', description: 'New password must be at least 6 characters', variant: 'destructive' });
      return;
    }
    setSavingCreds(true);
    try {
      const resp = await adminApi.put('/change-password', {
        current_password: creds.current_password,
        new_username: creds.new_username || '',
        new_password: creds.new_password || ''
      });
      const { token, username } = resp.data;
      localStorage.setItem('admin_token', token);
      localStorage.setItem('admin_user', JSON.stringify({ username, role: adminUser?.role || 'admin' }));
      adminApi.defaults.headers.common['Authorization'] = `Bearer ${token}`;
      setCreds({ current_password: '', new_username: '', new_password: '', confirm_password: '' });
      toast({ title: 'Success', description: 'Admin credentials updated. You may need to re-login with the new credentials.' });
    } catch (error) {
      const msg = error.response?.data?.detail || 'Failed to update credentials';
      toast({ title: 'Error', description: msg, variant: 'destructive' });
    } finally {
      setSavingCreds(false);
    }
  };


  const addContactCard = () => {
    const newCard = { icon: 'phone', title: { en: '', ar: '' }, content: { en: '', ar: '' }, sub_content: { en: '', ar: '' }, action: '', sub_action: '', action_label: { en: '', ar: '' }, order: (settings.contact_cards || []).length + 1 };
    setSettings({ ...settings, contact_cards: [...(settings.contact_cards || []), newCard] });
  };

  const updateContactCard = (idx, field, value) => {
    const cards = [...(settings.contact_cards || [])];
    cards[idx] = { ...cards[idx], [field]: value };
    setSettings({ ...settings, contact_cards: cards });
  };

  const updateCardBilingual = (idx, field, lang, value) => {
    const cards = [...(settings.contact_cards || [])];
    const current = cards[idx][field] || { en: '', ar: '' };
    if (typeof current === 'string') {
      cards[idx][field] = { en: lang === 'en' ? value : current, ar: lang === 'ar' ? value : '' };
    } else {
      cards[idx][field] = { ...current, [lang]: value };
    }
    setSettings({ ...settings, contact_cards: cards });
  };

  const moveCard = (idx, direction) => {
    const cards = [...(settings.contact_cards || [])];
    const targetIdx = idx + direction;
    if (targetIdx < 0 || targetIdx >= cards.length) return;
    [cards[idx], cards[targetIdx]] = [cards[targetIdx], cards[idx]];
    cards.forEach((c, i) => c.order = i + 1);
    setSettings({ ...settings, contact_cards: cards });
  };

  const removeContactCard = (idx) => {
    const cards = (settings.contact_cards || []).filter((_, i) => i !== idx);
    setSettings({ ...settings, contact_cards: cards });
  };

  if (loading) return <div className="flex items-center justify-center py-12"><Loader2 className="h-8 w-8 animate-spin text-red-600" /></div>;

  return (
    <div data-testid="admin-settings-page">
      <div className="mb-6">
        <h2 className="text-xl sm:text-3xl font-bold text-gray-900">Website Settings</h2>
        <p className="text-gray-600 mt-1">Configure your website appearance and information</p>
      </div>

      <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
        <Card>
          <CardHeader><CardTitle>General Information</CardTitle></CardHeader>
          <CardContent className="space-y-4">
            <div><Label>Site Name</Label><Input data-testid="settings-site-name" value={settings.site_name} onChange={(e) => setSettings({ ...settings, site_name: e.target.value })} /></div>
            <div><Label>Site Name (Arabic)</Label><Input value={settings.site_name_ar || ''} onChange={(e) => setSettings({ ...settings, site_name_ar: e.target.value })} dir="rtl" /></div>
            <div><Label>Tagline</Label><Input data-testid="settings-tagline" value={settings.tagline} onChange={(e) => setSettings({ ...settings, tagline: e.target.value })} /></div>
            <div><Label>Tagline (Arabic)</Label><Input value={settings.tagline_ar || ''} onChange={(e) => setSettings({ ...settings, tagline_ar: e.target.value })} dir="rtl" /></div>
            <div><Label>Logo URL</Label><Input value={settings.logo_url} onChange={(e) => setSettings({ ...settings, logo_url: e.target.value })} placeholder="https://..." /></div>
            <div><Label>Primary Color</Label><div className="flex gap-2"><Input type="color" value={settings.primary_color} onChange={(e) => setSettings({ ...settings, primary_color: e.target.value })} className="w-20" /><Input value={settings.primary_color} onChange={(e) => setSettings({ ...settings, primary_color: e.target.value })} /></div></div>
          </CardContent>
        </Card>

        <Card>
          <CardHeader><CardTitle>Contact Information</CardTitle></CardHeader>
          <CardContent className="space-y-4">
            <div><Label>Phone Number</Label><Input value={settings.phone} onChange={(e) => setSettings({ ...settings, phone: e.target.value })} /></div>
            <div><Label>Phone Number (Arabic)</Label><Input value={settings.phone_ar || ''} onChange={(e) => setSettings({ ...settings, phone_ar: e.target.value })} dir="rtl" /></div>
            <div><Label>Email Address</Label><Input value={settings.email} onChange={(e) => setSettings({ ...settings, email: e.target.value })} /></div>
            <div><Label>Order Notification Email</Label><Input type="email" value={settings.order_notification_email || ''} onChange={(e) => setSettings({ ...settings, order_notification_email: e.target.value })} placeholder={settings.email || 'orders@example.com'} /></div>
            <div><Label>WhatsApp Number</Label><Input value={settings.whatsapp} onChange={(e) => setSettings({ ...settings, whatsapp: e.target.value })} placeholder="97313619991" /></div>
            <div><Label>Address</Label><Textarea value={settings.address} onChange={(e) => setSettings({ ...settings, address: e.target.value })} rows={2} /></div>
            <div><Label>Address (Arabic)</Label><Textarea value={settings.address_ar || ''} onChange={(e) => setSettings({ ...settings, address_ar: e.target.value })} rows={2} dir="rtl" /></div>
            <div><Label>Working Hours</Label><Input value={settings.hours} onChange={(e) => setSettings({ ...settings, hours: e.target.value })} /></div>
            <div><Label>Working Hours (Arabic)</Label><Input value={settings.hours_ar || ''} onChange={(e) => setSettings({ ...settings, hours_ar: e.target.value })} dir="rtl" /></div>
          </CardContent>
        </Card>

        <Card>
          <CardHeader><CardTitle>Google Map Location</CardTitle></CardHeader>
          <CardContent className="space-y-4">
            <div className="grid grid-cols-2 gap-4">
              <div><Label>Latitude</Label><Input type="number" step="any" value={settings.map_lat} onChange={(e) => setSettings({ ...settings, map_lat: parseFloat(e.target.value) || 0 })} data-testid="settings-map-lat" /></div>
              <div><Label>Longitude</Label><Input type="number" step="any" value={settings.map_lng} onChange={(e) => setSettings({ ...settings, map_lng: parseFloat(e.target.value) || 0 })} data-testid="settings-map-lng" /></div>
            </div>
            <div><Label>Zoom Level (1-20)</Label><Input type="number" min="1" max="20" value={settings.map_zoom} onChange={(e) => setSettings({ ...settings, map_zoom: parseInt(e.target.value) || 15 })} /></div>
            <p className="text-xs text-gray-500">Find coordinates: Go to Google Maps, right-click on your location, and copy the coordinates.</p>
          </CardContent>
        </Card>

        <Card>
          <CardHeader><CardTitle>Social Media Links</CardTitle></CardHeader>
          <CardContent className="space-y-4">
            <div><Label>Facebook</Label><Input value={settings.facebook} onChange={(e) => setSettings({ ...settings, facebook: e.target.value })} /></div>
            <div><Label>Instagram</Label><Input value={settings.instagram} onChange={(e) => setSettings({ ...settings, instagram: e.target.value })} /></div>
            <div><Label>YouTube</Label><Input value={settings.youtube} onChange={(e) => setSettings({ ...settings, youtube: e.target.value })} /></div>
          </CardContent>
        </Card>

        <Card>
          <CardHeader><CardTitle>SEO Settings</CardTitle></CardHeader>
          <CardContent className="space-y-4">
            <div><Label>Meta Title</Label><Input value={settings.meta_title} onChange={(e) => setSettings({ ...settings, meta_title: e.target.value })} /></div>
            <div><Label>Meta Title (Arabic)</Label><Input value={settings.meta_title_ar || ''} onChange={(e) => setSettings({ ...settings, meta_title_ar: e.target.value })} dir="rtl" /></div>
            <div><Label>Meta Description</Label><Textarea value={settings.meta_description} onChange={(e) => setSettings({ ...settings, meta_description: e.target.value })} rows={3} /></div>
            <div><Label>Meta Description (Arabic)</Label><Textarea value={settings.meta_description_ar || ''} onChange={(e) => setSettings({ ...settings, meta_description_ar: e.target.value })} rows={3} dir="rtl" /></div>
            <div><Label>Keywords</Label><Input value={settings.keywords} onChange={(e) => setSettings({ ...settings, keywords: e.target.value })} /></div>
          </CardContent>
        </Card>

        <Card>
          <CardHeader><CardTitle>reCAPTCHA (Anti-spam)</CardTitle></CardHeader>
          <CardContent className="space-y-4">
            <div><Label>reCAPTCHA v3 Site Key</Label><Input value={settings.recaptcha_site_key} onChange={(e) => setSettings({ ...settings, recaptcha_site_key: e.target.value })} placeholder="6Lc..." data-testid="settings-recaptcha-site" /></div>
            <div><Label>reCAPTCHA v3 Secret Key</Label><Input type="password" value={settings.recaptcha_secret_key} onChange={(e) => setSettings({ ...settings, recaptcha_secret_key: e.target.value })} placeholder="6Lc..." data-testid="settings-recaptcha-secret" /></div>
            <div><Label>Google Analytics ID</Label><Input value={settings.google_analytics_id || ''} onChange={(e) => setSettings({ ...settings, google_analytics_id: e.target.value })} placeholder="G-XXXXXXXXXX" data-testid="settings-ga-id" /></div>
            <p className="text-xs text-gray-500">Get keys from <a href="https://www.google.com/recaptcha/admin" target="_blank" rel="noopener noreferrer" className="text-red-600 underline">Google reCAPTCHA Admin</a>. Choose reCAPTCHA v3.</p>
          </CardContent>
        </Card>
      </div>

      {/* Contact Cards */}
      <Card className="mt-6">
        <CardHeader>
          <div className="flex items-center justify-between">
            <CardTitle>Contact Page Cards</CardTitle>
            <Button size="sm" variant="outline" onClick={addContactCard} data-testid="add-contact-card-btn"><Plus className="h-4 w-4 mr-1" />Add Card</Button>
          </div>
          <p className="text-sm text-gray-500">Manage all cards shown on the Contact page. Drag to reorder, all fields are bilingual.</p>
        </CardHeader>
        <CardContent>
          {(settings.contact_cards || []).length === 0 && <p className="text-gray-400 text-sm">No cards. Click "Add Card" to create one.</p>}
          <div className="space-y-4">
            {(settings.contact_cards || []).map((card, idx) => {
              const getVal = (field, lang) => {
                const v = card[field];
                if (typeof v === 'object' && v !== null) return v[lang] || '';
                return lang === 'en' ? (v || '') : '';
              };
              return (
              <div key={idx} className="border rounded-lg p-4 space-y-3">
                <div className="flex justify-between items-center">
                  <div className="flex items-center gap-2">
                    <div className="flex flex-col gap-1">
                      <button type="button" onClick={() => moveCard(idx, -1)} disabled={idx === 0} className="text-gray-400 hover:text-gray-700 disabled:opacity-30 text-xs">▲</button>
                      <button type="button" onClick={() => moveCard(idx, 1)} disabled={idx === (settings.contact_cards || []).length - 1} className="text-gray-400 hover:text-gray-700 disabled:opacity-30 text-xs">▼</button>
                    </div>
                    <span className="text-sm font-semibold text-gray-700">Card {idx + 1}</span>
                  </div>
                  <Button size="sm" variant="ghost" className="text-red-500 h-7" onClick={() => removeContactCard(idx)}><Trash2 className="h-4 w-4" /></Button>
                </div>
                <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
                  <div><Label>Icon</Label>
                    <select value={card.icon || 'phone'} onChange={(e) => updateContactCard(idx, 'icon', e.target.value)} className="w-full rounded-md border px-3 py-2 text-sm">
                      <option value="map">Map / Location</option>
                      <option value="phone">Phone</option>
                      <option value="email">Email</option>
                      <option value="clock">Clock / Hours</option>
                      <option value="whatsapp">WhatsApp</option>
                      <option value="video">Video</option>
                      <option value="globe">Globe / Website</option>
                      <option value="message">Message</option>
                    </select>
                  </div>
                  <div><Label>Action URL</Label><Input value={card.action || ''} onChange={(e) => updateContactCard(idx, 'action', e.target.value)} placeholder="tel:+973... / https://..." /></div>
                </div>
                <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
                  <div><Label>Title (EN)</Label><Input value={getVal('title', 'en')} onChange={(e) => updateCardBilingual(idx, 'title', 'en', e.target.value)} placeholder="Our Location" /></div>
                  <div><Label>Title (AR)</Label><Input value={getVal('title', 'ar')} onChange={(e) => updateCardBilingual(idx, 'title', 'ar', e.target.value)} placeholder="موقعنا" dir="rtl" /></div>
                </div>
                <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
                  <div><Label>Content (EN)</Label><Input value={getVal('content', 'en')} onChange={(e) => updateCardBilingual(idx, 'content', 'en', e.target.value)} placeholder="Address or info..." /></div>
                  <div><Label>Content (AR)</Label><Input value={getVal('content', 'ar')} onChange={(e) => updateCardBilingual(idx, 'content', 'ar', e.target.value)} placeholder="العنوان أو المعلومات..." dir="rtl" /></div>
                </div>
                <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
                  <div><Label>Sub-Content (EN, optional)</Label><Input value={getVal('sub_content', 'en')} onChange={(e) => updateCardBilingual(idx, 'sub_content', 'en', e.target.value)} placeholder="Email or secondary info" /></div>
                  <div><Label>Sub-Content (AR, optional)</Label><Input value={getVal('sub_content', 'ar')} onChange={(e) => updateCardBilingual(idx, 'sub_content', 'ar', e.target.value)} dir="rtl" /></div>
                </div>
                <div className="grid grid-cols-1 sm:grid-cols-3 gap-3">
                  <div><Label>Sub-Action URL</Label><Input value={card.sub_action || ''} onChange={(e) => updateContactCard(idx, 'sub_action', e.target.value)} placeholder="mailto:..." /></div>
                  <div><Label>Button Label (EN)</Label><Input value={getVal('action_label', 'en')} onChange={(e) => updateCardBilingual(idx, 'action_label', 'en', e.target.value)} placeholder="Call Now" /></div>
                  <div><Label>Button Label (AR)</Label><Input value={getVal('action_label', 'ar')} onChange={(e) => updateCardBilingual(idx, 'action_label', 'ar', e.target.value)} placeholder="اتصل الآن" dir="rtl" /></div>
                </div>
              </div>
              );
            })}
          </div>
        </CardContent>
      </Card>

      {/* Admin Credentials */}
      <Card className="mt-6 border-amber-200 bg-amber-50/30">
        <CardHeader>
          <CardTitle className="flex items-center gap-2"><KeyRound className="h-5 w-5 text-amber-600" />Admin Credentials</CardTitle>
          <p className="text-sm text-gray-500">Change your admin username and/or password. Current admin: <strong>{adminUser?.username}</strong></p>
        </CardHeader>
        <CardContent>
          <div className="grid grid-cols-1 sm:grid-cols-2 gap-4 max-w-2xl">
            <div>
              <Label>Current Password *</Label>
              <Input type="password" value={creds.current_password} onChange={(e) => setCreds({ ...creds, current_password: e.target.value })} placeholder="Enter current password" data-testid="admin-current-password" />
            </div>
            <div>
              <Label>New Username (optional)</Label>
              <Input value={creds.new_username} onChange={(e) => setCreds({ ...creds, new_username: e.target.value })} placeholder={adminUser?.username || 'admin'} data-testid="admin-new-username" />
            </div>
            <div>
              <Label>New Password (optional, min 6 chars)</Label>
              <Input type="password" value={creds.new_password} onChange={(e) => setCreds({ ...creds, new_password: e.target.value })} placeholder="Leave blank to keep current" data-testid="admin-new-password" />
            </div>
            <div>
              <Label>Confirm New Password</Label>
              <Input type="password" value={creds.confirm_password} onChange={(e) => setCreds({ ...creds, confirm_password: e.target.value })} placeholder="Repeat new password" data-testid="admin-confirm-password" />
            </div>
          </div>
          <Button className="mt-4 bg-amber-600 hover:bg-amber-700" onClick={handleSaveCredentials} disabled={savingCreds || !creds.current_password} data-testid="save-admin-creds-btn">
            {savingCreds ? <Loader2 className="mr-2 h-4 w-4 animate-spin" /> : <KeyRound className="mr-2 h-4 w-4" />}
            {savingCreds ? 'Updating...' : 'Update Credentials'}
          </Button>
        </CardContent>
      </Card>

      <div className="mt-6 flex justify-end">
        <Button data-testid="save-settings-btn" className="bg-red-600 hover:bg-red-700" onClick={handleSave} disabled={saving}>
          {saving ? <Loader2 className="mr-2 h-4 w-4 animate-spin" /> : <Save className="mr-2 h-4 w-4" />}
          {saving ? 'Saving...' : 'Save All Settings'}
        </Button>
      </div>
    </div>
  );
};

export default AdminSettings;
